import {
  CHART_AXIS,
  CHART_CATEGORY_LABEL_LAYOUT,
  CHART_DIMENSIONS,
} from '../shared/chart-tokens';
import type {
  ChartCategoryLabelLayout,
  EffectiveCategoryLabelMode,
} from '../shared/chart-tokens';

import {
  CHART_X_AXIS_TICK_LABEL_ATTR,
  CHART_X_AXIS_TRUNCATE_PROBE_ATTR,
  FALLBACK_TICK_WIDTH,
  TICK_HEIGHT,
  getTickLabel,
  type TickPayload,
} from './bar-chart-tick-utils';

export interface BarChartXAxisTickProps {
  x?: number | string;
  y?: number | string;
  payload?: TickPayload;
  /** Total axis pixel width. Injected by Recharts. */
  width?: number | string;
  /** Total tick count. Injected by Recharts. */
  visibleTicksCount?: number;
  /** How the category label is rendered. Defaults to truncation. */
  categoryLabelMode?: EffectiveCategoryLabelMode;
  /** Renders an invisible horizontal probe for auto-mode fit measurement. */
  includeTruncateProbe?: boolean;
  /** Forwarded by Recharts for custom tick rendering. */
  className?: string;
}

/** Computes the pixel width available for a single tick label. */
const computeTickLabelWidth = (
  axisWidth: number | string | undefined,
  visibleTicksCount: number | undefined
): number => {
  const numericAxisWidth = Number(axisWidth);

  if (
    !Number.isFinite(numericAxisWidth) ||
    visibleTicksCount === undefined ||
    visibleTicksCount <= 0
  ) {
    return FALLBACK_TICK_WIDTH;
  }

  const slotWidth = numericAxisWidth / visibleTicksCount;

  return Math.max(slotWidth - CHART_DIMENSIONS.xAxisLabelGap, 0);
};

const AngledTickLabel = ({
  label,
  angle,
  textAnchor,
  className,
}: {
  label: string;
  angle: number;
  textAnchor: ChartCategoryLabelLayout['textAnchor'];
  className?: string;
}) => {
  const offsetY = CHART_DIMENSIONS.xAxisLabelGap;

  return (
    <text
      x={0}
      y={offsetY}
      transform={`rotate(${angle}, 0, ${offsetY})`}
      textAnchor={textAnchor}
      dominantBaseline="middle"
      fill={CHART_AXIS.tickColor}
      fontFamily={CHART_AXIS.tickFontFamily}
      fontSize={CHART_AXIS.tickFontSize}
      className={className}
      {...{ [CHART_X_AXIS_TICK_LABEL_ATTR]: 'true' }}
    >
      {label}
    </text>
  );
};

const HorizontalTickLabel = ({
  label,
  layout,
  tickWidth,
  categoryLabelMode,
  isProbe = false,
  ...dataAttrs
}: {
  label: string;
  layout: ChartCategoryLabelLayout;
  tickWidth: number;
  categoryLabelMode: EffectiveCategoryLabelMode;
  isProbe?: boolean;
  [key: `data-${string}`]: string;
}) => (
  <foreignObject
    x={-tickWidth / 2}
    y={0}
    width={tickWidth}
    height={
      categoryLabelMode === 'wrap' ? layout.xAxisLabelHeight : TICK_HEIGHT
    }
    aria-hidden={isProbe || undefined}
    style={
      isProbe ? { visibility: 'hidden', pointerEvents: 'none' } : undefined
    }
  >
    <div
      title={isProbe ? undefined : label}
      {...{
        [CHART_X_AXIS_TICK_LABEL_ATTR]: isProbe ? undefined : 'true',
        [CHART_X_AXIS_TRUNCATE_PROBE_ATTR]: isProbe ? 'true' : undefined,
        ...dataAttrs,
      }}
      className={`font-text text-xs text-gray-500 text-center${
        categoryLabelMode === 'wrap' ? '' : ' truncate'
      }`}
      style={
        categoryLabelMode === 'wrap'
          ? {
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: layout.maxLines,
              overflow: 'hidden',
              overflowWrap: 'anywhere',
              whiteSpace: 'normal',
            }
          : undefined
      }
    >
      {label}
    </div>
  </foreignObject>
);

export const BarChartXAxisTick = ({
  x = 0,
  y = 0,
  payload,
  width: axisWidth,
  visibleTicksCount,
  categoryLabelMode = 'truncate',
  includeTruncateProbe = false,
  className,
}: BarChartXAxisTickProps) => {
  const fullLabel = getTickLabel(payload);
  const numericX = Number(x);
  const numericY = Number(y);

  if (!fullLabel || !Number.isFinite(numericX) || !Number.isFinite(numericY)) {
    return null;
  }

  const layout = CHART_CATEGORY_LABEL_LAYOUT[categoryLabelMode];
  const tickWidth = computeTickLabelWidth(axisWidth, visibleTicksCount);

  return (
    <g transform={`translate(${numericX}, ${numericY})`}>
      {layout.angle !== null && (
        <AngledTickLabel
          label={fullLabel}
          angle={layout.angle}
          textAnchor={layout.textAnchor}
          className={className}
        />
      )}
      {layout.angle === null && (
        <HorizontalTickLabel
          label={fullLabel}
          layout={layout}
          tickWidth={tickWidth}
          categoryLabelMode={categoryLabelMode}
          {...{
            [CHART_X_AXIS_TRUNCATE_PROBE_ATTR]: includeTruncateProbe
              ? 'true'
              : undefined,
          }}
        />
      )}
      {includeTruncateProbe && layout.angle !== null && (
        <HorizontalTickLabel
          label={fullLabel}
          layout={CHART_CATEGORY_LABEL_LAYOUT.truncate}
          tickWidth={tickWidth}
          categoryLabelMode="truncate"
          isProbe={true}
        />
      )}
    </g>
  );
};
